import { ChefHat, Clock3, Flame, Share2, ShoppingBasket, X } from "lucide-react";

import { Button } from "@/components/ui/button";


export const RecipeDrawer = ({ open, onOpenChange, recipe, onGoToGrocery, onShareRecipe, onStartCooking, sourceHint }) => {
  if (!open || !recipe) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[120]" data-testid="recipe-drawer-content">
      <button
        type="button"
        className="absolute inset-0 bg-stone-900/45 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
        data-testid="recipe-drawer-backdrop"
      />

      <div className="absolute bottom-0 left-1/2 flex max-h-[86vh] w-full max-w-md -translate-x-1/2 flex-col rounded-t-[28px] border border-white/70 bg-[#fbf7ef] shadow-[0_-24px_60px_rgba(57,72,43,0.22)]" role="dialog" aria-modal="true">
        <div className="flex items-center justify-between border-b border-stone-200 px-4 py-4">
          <div>
            <h2 className="text-2xl font-semibold text-stone-900" data-testid="recipe-drawer-title">
              {recipe.title}
            </h2>
            <p className="mt-1 text-sm text-stone-600" data-testid="recipe-drawer-description">
              {recipe.description}
            </p>
          </div>
          <Button variant="outline" className="h-10 w-10 rounded-full border-stone-200 bg-white p-0" onClick={() => onOpenChange(false)} data-testid="recipe-drawer-close-button">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-6 pt-4">
          <div className="mb-4 flex flex-wrap gap-2">
            {onStartCooking ? (
              <Button className="h-12 w-fit min-w-[190px] rounded-full bg-stone-900 text-white hover:bg-stone-800" onClick={onStartCooking} data-testid="recipe-drawer-cook-button">
                <ChefHat className="mr-2 h-4 w-4" />
                Start cook mode
              </Button>
            ) : null}
            {onShareRecipe ? (
              <Button variant="outline" className="h-12 rounded-full border-stone-200 bg-white" onClick={onShareRecipe} data-testid="recipe-drawer-share-button">
                <Share2 className="mr-2 h-4 w-4" />
                Deel recept
              </Button>
            ) : null}
          </div>

          {sourceHint ? (
            <div className="mb-4 rounded-[20px] border border-dashed border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="recipe-drawer-source-note">
              {sourceHint}
            </div>
          ) : null}

          <img
            src={recipe.image_url}
            alt={recipe.title}
            className="aspect-[4/3] w-full rounded-[24px] object-cover"
            data-testid="recipe-drawer-image"
          />

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-[22px] border border-stone-200 bg-white/90 p-4" data-testid="recipe-drawer-kcal-card">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-stone-500">
                <Flame className="h-4 w-4 text-orange-500" />
                Energie
              </div>
              <p className="mt-3 text-2xl font-semibold text-stone-900" data-testid="recipe-drawer-kcal-value">
                {recipe.kcal_per_serving} kcal
              </p>
            </div>
            <div className="rounded-[22px] border border-stone-200 bg-white/90 p-4" data-testid="recipe-drawer-carbs-card">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-stone-500">
                <Flame className="h-4 w-4 text-emerald-700" />
                Carbs
              </div>
              <p className="mt-3 text-2xl font-semibold text-stone-900" data-testid="recipe-drawer-carbs-value">
                {recipe.carbs_per_serving} g
              </p>
            </div>
            <div className="rounded-[22px] border border-stone-200 bg-white/90 p-4" data-testid="recipe-drawer-time-card">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-stone-500">
                <Clock3 className="h-4 w-4 text-emerald-700" />
                Tijd
              </div>
              <p className="mt-3 text-2xl font-semibold text-stone-900" data-testid="recipe-drawer-time-value">
                {recipe.prep_time_min + recipe.cook_time_min} min
              </p>
            </div>
          </div>

          <section className="mt-5 rounded-[24px] border border-stone-200 bg-white/90 p-4" data-testid="recipe-drawer-ingredients-section">
            <h3 className="text-sm font-semibold uppercase tracking-[0.28em] text-stone-500" data-testid="recipe-drawer-ingredients-title">
              Ingrediënten
            </h3>
            <ul className="mt-4 space-y-3">
              {recipe.ingredients.map((ingredient) => (
                <li key={`${recipe.id}-${ingredient.ingredient_id}`} className="flex items-center justify-between gap-4 text-sm text-stone-700" data-testid={`recipe-drawer-ingredient-${ingredient.ingredient_slug}`}>
                  <span>{ingredient.ingredient_name}</span>
                  <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-600">
                    {ingredient.quantity} {ingredient.unit}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-5 rounded-[24px] border border-stone-200 bg-white/90 p-4" data-testid="recipe-drawer-steps-section">
            <h3 className="text-sm font-semibold uppercase tracking-[0.28em] text-stone-500" data-testid="recipe-drawer-steps-title">
              Bereiding
            </h3>
            <ol className="mt-4 space-y-3">
              {recipe.instructions.map((step, index) => (
                <li key={`${recipe.id}-step-${index + 1}`} className="flex gap-3 text-sm text-stone-700" data-testid={`recipe-drawer-step-${index + 1}`}>
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-700 text-xs font-semibold text-white">
                    {index + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <div className="glass-panel border-t border-stone-200 px-4 pb-6 pt-4">
          <Button className="h-12 w-fit min-w-[220px] rounded-full bg-emerald-700 text-white hover:bg-emerald-800" onClick={onGoToGrocery} data-testid="recipe-drawer-grocery-button">
            <ShoppingBasket className="mr-2 h-4 w-4" />
            Open boodschappenlijst
          </Button>
        </div>
      </div>
    </div>
  );
};